// UnitPermissionSummaryDialog - MODULE
// ***********************************************************************************************************************
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import * as React from "react";
import { UserGroupType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IUnitNode, UnitNode } from "../../../../models/unit/unitNode";
import { IUnitSummaryNode, UnitSummaryNode } from "../../../../models/unit/unitSummaryNode";
import { IUnitPermissionUserGroup, UnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { ActionContainer } from "../../../framework/ActionContainer";
import { Dialog } from "../../../framework/Dialog/Dialog";
import { ListSearchFilter } from "../../../framework/List/ListSearchFilter";
import { UnitPermissionSummaryTable } from "../UnitPermissionSummaryTable";
import { UnitApplicationPermissions } from "source/components/framework/Unit/UnitApplicationPermissions";

//UnitPermissionSummaryDialog
export interface IUnitPermissionSummaryDialogProp {
    classes?: string;
    groups: IUnitPermissionUserGroup[];
    hideAllUsers?: boolean;
    hideUserGroups?: boolean;
    unitNode: IUnitNode;
    accessRightNoneTitle: string;
    accessRightReadTitle: string;
    accessRightWriteTitle: string;
    additionalPermissionTitle: string[];
    validAdditionalPermissions: number[];
    onCancel: () => void;
}

export interface IUnitPermissionSummaryDialogState {
    summaryNode: IUnitSummaryNode;
    additionalPermissionInUse: boolean[];
    additionalPermissionsInUse: boolean;
    showInheritedPermissions: boolean;
    showUserGroups: boolean;
    showUsers: boolean;
    filter: string;
}

export class UnitPermissionSummaryDialog extends React.Component<IUnitPermissionSummaryDialogProp, IUnitPermissionSummaryDialogState> {
    getBaseUnitSummaryNode = (unitNode: IUnitNode): UnitSummaryNode => {
        const result = new UnitSummaryNode();
        result.id = unitNode.id;
        result.name = unitNode.name;
        for (const child of unitNode.children) {
            result.children.push(this.getBaseUnitSummaryNode(child));
        }
        return result;
    };

    getUnitSummaryNode = (unitNode: IUnitNode, userGroups: IUnitPermissionUserGroup[]): UnitSummaryNode => {
        const result = this.getBaseUnitSummaryNode(unitNode);
        for (const userGroup of userGroups) {
            const userGroupUnitNode = new UnitNode(unitNode);
            userGroupUnitNode.setEffectivePermissions(null, userGroup);
            result.setUserGroupPermissions(userGroup, userGroupUnitNode);
        }
        return result;
    };

    constructor(props: IUnitPermissionSummaryDialogProp) {
        super(props);
        const additionalPermissionInUse = [
            !!props.additionalPermissionTitle[0] && props.unitNode.maxPermissionLevelA > 0,
            !!props.additionalPermissionTitle[1] && props.unitNode.maxPermissionLevelB > 0,
            !!props.additionalPermissionTitle[2] && props.unitNode.maxPermissionLevelC > 0
        ];
        this.state = {
            summaryNode: null,
            additionalPermissionInUse,
            additionalPermissionsInUse: additionalPermissionInUse[0] || additionalPermissionInUse[1] || additionalPermissionInUse[2],
            showInheritedPermissions: false,
            showUserGroups: true,
            showUsers: true,
            filter: ""
        };
    }

    componentDidMount(): void {
        const props = this.props;
        const groups = props.groups.map(i => new UnitPermissionUserGroup(i));
        //const userGroups = UnitApplicationPermissions.getUserGroups(groups, !props.hideAllUsers, !props.hideUserGroups ? UserGroupType.Normal : UserGroupType.Fixed);
        const userGroups: IUnitPermissionUserGroup[] = (!props.hideAllUsers ? UnitApplicationPermissions.getUserGroups(groups, true, UserGroupType.Normal) : [])
            .concat(!props.hideUserGroups ? UnitApplicationPermissions.getUserGroups(groups, false, UserGroupType.Normal) : [])
            .concat(UnitApplicationPermissions.getUserGroups(groups, false, UserGroupType.Fixed));
        this.setState({
            summaryNode: this.getUnitSummaryNode(props.unitNode, userGroups)
        });
    }

    getAdditionalPermissionIsDisabled = (permission: number, additionalPermissionIndex: number) :boolean => {
        const props = this.props;
        const index = permission * 3 + additionalPermissionIndex;
        if (props.validAdditionalPermissions.length <= index) return true;
        return props.validAdditionalPermissions[index] < 1;
    };

    getAdditionalPermissionInUse = (permission: number, additionalPermissionIndex: number): boolean => {
        const state = this.state;
        return state.additionalPermissionInUse[additionalPermissionIndex] && !this.getAdditionalPermissionIsDisabled(permission, additionalPermissionIndex);
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.summaryNode) return null;
        return (
            <Dialog
                id="unit-permission-summary-dialog"
                maxWidth="md"
                title={Translations.SetPermissions}
                centerOnResize
                body={
                    <Box sx={{ margin: "-1rem 0" }}>
                        <Stack direction="row" justifyContent="space-between">
                            <FormControlLabel
                                label={Translations.ShowInheritedPermissions}
                                control={
                                    <Checkbox
                                        checked={state.showInheritedPermissions}
                                        onChange={(_e, checked: boolean) => {
                                            this.setState({ showInheritedPermissions: checked });
                                        }}
                                    />
                                }
                                sx={{ marginBottom: ".5rem" }}
                            />
                            {!props.hideUserGroups &&
                                <FormControlLabel
                                    label={Translations.ShowUserGroups}
                                    control={
                                        <Checkbox
                                            checked={state.showUserGroups}
                                            onChange={(_e, checked: boolean) => {
                                                this.setState({ showUserGroups: checked });
                                            }}
                                        />
                                    }
                                />
                            }
                            {!props.hideUserGroups && !props.hideAllUsers &&
                                <FormControlLabel
                                    label={Translations.ShowUsers}
                                    control={
                                        <Checkbox
                                            checked={state.showUsers}
                                            onChange={(_e, checked: boolean) => {
                                                this.setState({ showUsers: checked });
                                            }}
                                        />
                                    }
                                />
                            }
                        </Stack>
                        <ActionContainer leftItems={
                            <ListSearchFilter
                                inline={true}
                                active={!!state.filter}
                                searchFilter={state.filter}
                                onSearchClick={(filter: string) => this.setState({ filter })}
                            />
                        } />
                        <Box sx={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto", marginRight: "-1.5rem", paddingRight: "1rem" }}>
                            <UnitPermissionSummaryTable
                                unitNode={state.summaryNode}
                                parentName={""}
                                accessRightNoneTitle={props.accessRightNoneTitle}
                                accessRightReadTitle={props.accessRightReadTitle}
                                accessRightWriteTitle={props.accessRightWriteTitle}
                                additionalPermissionTitle={props.additionalPermissionTitle}
                                additionalPermissionsInUse={state.additionalPermissionsInUse}
                                showInheritedPermissions={state.showInheritedPermissions}
                                showUserGroups={state.showUserGroups}
                                showUsers={state.showUsers}
                                filter={state.filter}
                                getAdditionalPermissionInUse={this.getAdditionalPermissionInUse}
                            />
                        </Box>
                    </Box>
                }
                buttons={[
                    { title: Translations.Close, variant: "outlined", onClick: props.onCancel }
                ]}
                onClose={props.onCancel}
            />
        );
    }
}